import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { Smile, Trash2, MoreHorizontal } from 'lucide-react';
import EmojiPicker, { Theme, EmojiClickData } from 'emoji-picker-react';
import { getInitialTheme } from '@/utils/theme';

type ChatMessageItemProps = {
  message: {
    id: string;
    idn?: string;
    sender: string;
    text: string;
    timestamp: string;
    imageUrl?: string;
    linkPreview?: {
      title: string;
      description: string;
      image?: string;
      url: string;
    };
    reactions?: { [userId: string]: string };
  };
  userId: string;
  isOtherUser: boolean;
  senderName?: string;
  senderAvatar?: string;
  showSenderInfo: boolean;
  hideTimestamp?: boolean;
  onDeleteMessage: (messageId: string) => void;
  onAddReaction: (messageId: string, emoji: string) => void;
};

export const ChatMessageItem: React.FC<ChatMessageItemProps> = ({
  message,
  userId,
  isOtherUser,
  senderName = 'Unknown User',
  senderAvatar = '/assets/images/fallback.jpg',
  showSenderInfo,
  hideTimestamp = false,
  onDeleteMessage,
  onAddReaction,
}) => {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const emojiPickerTheme = getInitialTheme();
  const isSingleEmoji = /^\p{Emoji}$/u.test(message.text);

  // Get unique reactions and their counts
  const reactionCounts = message.reactions
    ? Object.values(message.reactions).reduce((acc, emoji) => {
      acc[emoji] = (acc[emoji] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number })
    : {};

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours >= 24) {
      // If more than 24 hours old, show date and time
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) + 
             ' ' + 
             date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } else {
      // If less than 24 hours old, show only time
      return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
  };

  const handleReaction = (emojiData: EmojiClickData) => {
    onAddReaction(message.id, emojiData.emoji);
    setShowReactionPicker(false);
  };

  const renderEmoji = (text: string) => {
    return <span className="mb-2 text-5xl">{text}</span>;
  };

  const renderLinkPreview = (linkPreview: any) => {
    return (
      <a href={linkPreview.url} target="_blank" rel="noopener noreferrer" className="flex max-w-60 flex-col rounded-lg bg-green-600 p-1">
        {linkPreview.image && (
          <img src={linkPreview.image} alt="Link Preview" className="rounded-lg" />
        )}
        <h3 className="font-bold text-white">{linkPreview.title}</h3>
        <p className="text-white">{linkPreview.description}</p>
      </a>
    );
  };

  return (
    <div className={`flex flex-col ${isOtherUser ? 'items-start' : 'items-end'} ${!showSenderInfo ? 'mt-1' : 'mt-3'}`}>
      {isOtherUser && showSenderInfo && (
        <p className="mb-1 ml-10 text-xs font-semibold text-primary">{senderName}</p>
      )}
      <div className={`relative flex gap-2 max-w-[90%] ${isOtherUser ? 'justify-start' : 'justify-end'} w-full`}>
        {isOtherUser ? (
          showSenderInfo ? (
            <GolfAvatar size={40} imageUrl={senderAvatar} />
          ) : (
            <div className="w-10 flex-shrink-0"></div>
          )
        ) : null}
        <div className="relative">
          <div
            className={`relative rounded-full ${message.linkPreview || message.imageUrl ? 'p-1' : 'px-4 py-2'} ${isSingleEmoji || message.imageUrl
              ? ''
              : isOtherUser
                ? 'bg-secondary text-black dark:bg-neutral-700 dark:text-white'
                : 'bg-primary text-white'
              }`}
          >
            {message.imageUrl && (
              <img src={message.imageUrl} alt="Chat image" className="object-cover rounded-lg max-h-60" />
            )}
            {message.linkPreview && renderLinkPreview(message.linkPreview)}
            {message.text && (isSingleEmoji ? renderEmoji(message.text) : <p>{message.text}</p>)}
            {!hideTimestamp && (
              <p
                className={`mt-1 text-right text-xs ${message.sender === userId && !(isSingleEmoji || message.imageUrl) ? 'text-white' : 'text-foreground'
                  }`}
              >
                {formatTimestamp(message.timestamp)}
              </p>
            )}

            {/* Reactions Display */}
            {Object.keys(reactionCounts).length > 0 && (
              <div
                className={`absolute ${isOtherUser ? 'right-0 translate-x-1/4' : 'left-0 -translate-x-1/4'
                  } bottom-0 flex translate-y-1/2 -space-x-1 rounded-full bg-card px-2 py-0.5 shadow-md`}
              >
                {Object.entries(reactionCounts).map(([emoji, count]) => (
                  <div key={emoji} className="flex items-center">
                    <span className="text-sm">{emoji}</span>
                    {count > 1 && <span className="ml-1 text-xs text-muted-foreground">{count}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div
            className={`invisible absolute top-1/2 flex -translate-y-1/2 gap-1 rounded-full bg-background p-1 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 ${isOtherUser ? 'left-[calc(100%+8px)]' : 'right-[calc(100%+8px)]'
              }`}
          >
            {/* Reaction Picker */}
            <div className="relative justify-center">
              <div className="absolute bottom-full z-50 mb-2 translate-x-[-45%]">
                {showReactionPicker && (
                  <EmojiPicker
                    theme={emojiPickerTheme === 'light' ? Theme.LIGHT : Theme.DARK}
                    reactionsDefaultOpen
                    onEmojiClick={handleReaction}
                    searchDisabled
                    skinTonesDisabled
                    allowExpandReactions={false}
                    previewConfig={{ showPreview: false }}
                    height={150}
                    width={250}
                  />
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => setShowReactionPicker(!showReactionPicker)}
              >
                <Smile className="size-5" />
              </Button>
            </div>
            {message.sender === userId && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteMessage(message.id);
                }}
              >
                <Trash2 className="size-5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="size-7">
              <MoreHorizontal className="size-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
